import { Bell, ExternalLink } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import notificationContactsService from "../../services/notificationContactsService";
import type { CommunicationEndpointsResponse } from "../../types/adminNotifications";
import { describeNotificationError } from "../../utils/adminNotificationForm";
import NotificationEndpointsForm from "./NotificationEndpointsForm";

interface UserNotificationContactsCardProps {
  userId: string;
  loginEmail: string;
  isDarkMode: boolean;
}

/**
 * Users sidebar card: the selected user's notification email and SMS
 * destinations (PWA-2690). Every request is guarded by a token keyed to the
 * selected user so a slower earlier user's response can never land on the
 * newer selection.
 */
const UserNotificationContactsCard: React.FC<UserNotificationContactsCardProps> = ({
  userId,
  loginEmail,
}) => {
  const [endpoints, setEndpoints] = useState<CommunicationEndpointsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  const load = useCallback(async (targetUserId: string) => {
    const reqId = ++reqRef.current;
    setEndpoints(null);
    setError(null);
    setLoading(true);
    try {
      const result = await notificationContactsService.getUserEndpoints(targetUserId);
      if (reqId !== reqRef.current) return;
      setEndpoints(result);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      const described = describeNotificationError(err);
      setError(
        described.kind === "not-found"
          ? "User not found in the notification service."
          : described.message
      );
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(userId);
    return () => {
      // Invalidate in-flight work for the previous user immediately on switch/unmount.
      reqRef.current += 1;
    };
  }, [userId, load]);

  const historyHref = `/admin/notifications/users?u_q=${encodeURIComponent(userId)}`;

  return (
    <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
      <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
        <div className="flex items-center gap-2">
          <Bell className={`w-4 h-4 text-v7-ink/60`} />
          <h4 className={`text-sm font-medium text-v7-ink-soft`}>
            Notifications
          </h4>
        </div>
        <Link
          to={historyHref}
          className={`text-xs px-2 py-1 rounded-v7-control flex items-center gap-1 text-v7-bronze-dark hover:bg-v7-paper`}
        >
          Open in Notifications
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
      <div className="p-3">
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25"></div>
            <span className={`text-sm text-v7-ink/60`}>Loading...</span>
          </div>
        ) : error ? (
          <div className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 px-3 py-2">
            <p className="text-xs text-v7-rust">{error}</p>
            <button
              type="button"
              onClick={() => load(userId)}
              className="mt-1 text-xs font-semibold text-v7-rust underline"
            >
              Retry
            </button>
          </div>
        ) : endpoints ? (
          <NotificationEndpointsForm appearance="alpha"
            userId={userId}
            loginEmail={loginEmail}
            endpoints={endpoints}
            onChange={next => setEndpoints(current => (current ? { ...current, ...next } : current))}
            onRefresh={() => load(userId)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default UserNotificationContactsCard;
